import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { SEOHead, JsonLd } from "@/components/SEO";
import { VendorCard, ReviewSnippet } from "@/pages/seo/_components";
import { MapPin, Calendar, Layers } from "lucide-react";

/**
 * /vendors/{city-slug} — e.g. /vendors/washington-dc
 *
 * Owns local "vendors near me" intent for that metro. Renders: count + intro,
 * category breakdown (cross-links to category×city), top-rated vendors,
 * upcoming events in city, all vendors, recent reviews. JSON-LD: Place +
 * BreadcrumbList + ItemList.
 */
export default function VendorCityPage() {
    const { slugOrId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/seo/cities/${slugOrId}`);
                if (!cancelled) setData(r.data);
            } catch (e) { if (!cancelled) setErr(e); }
        })();
        return () => { cancelled = true; };
    }, [slugOrId]);

    if (err) return <Shell><div className="p-10 text-brand-mute">Failed to load.</div></Shell>;
    if (!data) return <Shell><div className="p-10 text-brand-mute">Loading…</div></Shell>;

    const loc = data.location;
    const cityLabel = `${loc.city}${loc.state ? `, ${loc.state}` : ""}`;
    const n = data.vendor_count;
    const titleCore = `Health fair vendors in ${cityLabel}`;
    const description = `${n} healthcare and wellness vendor${n === 1 ? "" : "s"} on HealthFairX serving ${cityLabel}-area corporate health fairs. Filter by category, read host reviews, and book your event.`;

    const breadcrumb = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: typeof window !== "undefined" ? window.location.origin : "" },
            { "@type": "ListItem", position: 2, name: "Vendors", item: typeof window !== "undefined" ? `${window.location.origin}/vendors` : "" },
            { "@type": "ListItem", position: 3, name: cityLabel },
        ],
    };
    const itemList = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: titleCore,
        numberOfItems: n,
        itemListElement: data.vendors.slice(0, 25).map((v, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: typeof window !== "undefined" ? `${window.location.origin}/vendors/${v.vendor_slug || v.id}` : undefined,
            name: v.business_name,
        })),
    };

    return (
        <Shell>
            <SEOHead title={titleCore} description={description} />
            <JsonLd data={breadcrumb} />
            <JsonLd data={itemList} />

            <section className="bg-brand-bone border-b border-brand-line">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
                    <div className="text-xs uppercase tracking-wider text-brand-mute font-bold flex items-center gap-2">
                        <Link to="/vendors" className="hover:text-brand-forest">Vendors</Link>
                        <span>/</span>
                        <span className="text-brand-ink">{cityLabel}</span>
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mt-3 flex items-center gap-3" data-testid="seo-city-h1">
                        <MapPin size={36} className="text-brand-forest" /> Vendors in {cityLabel}
                    </h1>
                    <p className="mt-3 text-brand-mute max-w-3xl">
                        {n > 0
                            ? `${n} vetted healthcare and wellness vendor${n === 1 ? "" : "s"} on HealthFairX ready to serve ${cityLabel}-area corporate health fairs.`
                            : `No vendors are listed in ${cityLabel} yet.`}
                    </p>
                    <div className="mt-5 flex flex-wrap gap-3 text-sm">
                        <Link to={`/health-fairs/${loc.slug}`} data-testid="city-link-health-fairs" className="pill px-4 py-2 font-semibold bg-white border border-brand-line hover:border-brand-forest hover:bg-brand-mint">
                            Health fairs in {cityLabel} →
                        </Link>
                    </div>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 space-y-12">
                {/* Category breakdown — cross-link to category×city pages (SEO gold) */}
                {data.categories.length > 0 && (
                    <section data-testid="seo-city-categories">
                        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                            <Layers size={20} className="text-brand-forest" /> Vendor categories in {cityLabel}
                        </h2>
                        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mt-5">
                            {data.categories.map((c) => (
                                <Link key={c.id} to={`/vendors/${loc.slug}/${c.slug}`}
                                      data-testid={`seo-city-cat-${c.slug}`}
                                      className="rounded-xl border border-brand-line bg-white p-4 hover:border-brand-forest hover:bg-brand-mint/40 transition flex items-baseline justify-between gap-3">
                                    <span className="font-semibold">{c.name}</span>
                                    <span className="text-xs text-brand-mute font-bold">{c.vendor_count}</span>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Upcoming events */}
                {data.upcoming_events.length > 0 && (
                    <section data-testid="seo-city-events">
                        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                            <Calendar size={20} className="text-brand-forest" /> Upcoming health fairs in {cityLabel}
                        </h2>
                        <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                            {data.upcoming_events.map((ev) => (
                                <li key={ev.event_id} className="rounded-xl border border-brand-line bg-white p-4">
                                    <Link to={`/events/${ev.event_slug || ev.event_id}`} className="font-semibold hover:underline block">{ev.name}</Link>
                                    <div className="text-xs text-brand-mute mt-1">{ev.event_date}</div>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Top-rated */}
                {data.top_rated.length > 0 && (
                    <section data-testid="seo-city-top-rated">
                        <h2 className="font-heading text-2xl font-bold">Top-rated vendors in {cityLabel}</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
                            {data.top_rated.map((v) => <VendorCard key={v.id} v={v} testid={`seo-city-top-${v.id}`} />)}
                        </div>
                    </section>
                )}

                {/* All vendors */}
                {data.vendors.length > 0 && (
                    <section data-testid="seo-city-all">
                        <h2 className="font-heading text-2xl font-bold">All vendors serving {cityLabel}</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
                            {data.vendors.map((v) => <VendorCard key={v.id} v={v} />)}
                        </div>
                    </section>
                )}

                {/* Recent reviews */}
                {data.recent_reviews.length > 0 && (
                    <section data-testid="seo-city-reviews">
                        <h2 className="font-heading text-2xl font-bold">Recent reviews from {cityLabel} hosts</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
                            {data.recent_reviews.map((r) => <ReviewSnippet key={r.id} r={r} />)}
                        </div>
                    </section>
                )}
            </div>
        </Shell>
    );
}
